import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import logoImage from "@assets/logo-e-zez-png-3.png";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { format, parseISO } from 'date-fns';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  CalendarIcon, 
  ClockIcon, 
  UserIcon, 
  MailIcon, 
  PhoneIcon, 
  ClipboardCheckIcon, 
  XIcon,
  CheckIcon,
  XCircleIcon,
  MessageSquareIcon
} from 'lucide-react';

// Interface for booking data
interface Booking {
  id: number;
  name: string;
  email: string;
  phone: string;
  date: string;
  time: string;
  service: string;
  message?: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
}

export default function BookingManagement() {
  const [, navigate] = useLocation();
  const { user, logoutMutation } = useAuth();
  const { toast } = useToast();
  
  // State for selected booking and response
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseMethod, setResponseMethod] = useState<'email' | 'sms'>('email');
  const [isSending, setIsSending] = useState(false);

  // State for status update
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // Fetch bookings data
  const { data: bookings, isLoading, refetch } = useQuery({
    queryKey: ['/api/bookings'],
    queryFn: async () => {
      try {
        // Authenticated by the session cookie, same as every other admin call.
        const response = await fetch('/api/bookings', {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        
        if (!response.ok) {
          console.error('Failed to fetch bookings from API:', response.status);
          throw new Error('Failed to fetch bookings');
        }
        
        const data = await response.json();
        return data.data as Booking[];
      } catch (error) {
        console.error('Error in booking fetch:', error);
        throw error;
      }
    }
  });

  // Function to handle status update
  const handleStatusUpdate = async () => {
    if (!selectedBooking || !newStatus) return;
    
    setIsUpdatingStatus(true);
    
    try {
      // Store original status to check if changed to confirmed
      const originalStatus = selectedBooking.status;
      const customerEmail = selectedBooking.email;
      
      const response = await fetch(`/api/bookings/${selectedBooking.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to update booking status');
      }
      
      // Show different toast based on status change
      if (originalStatus !== 'confirmed' && newStatus === 'confirmed') {
        toast({
          title: "Appointment Confirmed!",
          description: `Confirmation email sent to ${customerEmail}`,
          variant: "default",
        });
      } else {
        toast({
          title: "Status Updated",
          description: `Booking status has been updated to ${newStatus}`,
        });
      }
      
      // Close dialog and refetch data
      setStatusUpdateOpen(false);
      setSelectedBooking(null);
      setNewStatus('');
      refetch();
      
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Failed to update booking status",
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Function to send response to customer
  const handleSendResponse = async () => {
    if (!selectedBooking || !responseMessage) return;
    
    setIsSending(true);
    
    try {
      const response = await fetch(`/api/bookings/${selectedBooking.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          message: responseMessage,
          method: responseMethod,
          bookingId: selectedBooking.id
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send response');
      }
      
      toast({
        title: "Response Sent",
        description: `Your response has been sent to the customer via ${responseMethod}`,
      });
      
      // Close dialog and reset form
      setResponseDialogOpen(false);
      setResponseMessage('');
      setSelectedBooking(null);
      
    } catch (error) {
      console.error('Error sending response:', error);
      toast({
        variant: "destructive",
        title: "Failed to Send",
        description: error instanceof Error ? error.message : "Failed to send response to customer",
      });
    } finally {
      setIsSending(false);
    }
  };

  // Function to get status badge style
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return "bg-green-100 text-green-800 border border-green-200";
      case 'cancelled':
        return "bg-red-100 text-red-800 border border-red-200";
      case 'completed':
        return "bg-blue-100 text-blue-800 border border-blue-200";
      case 'pending':
      default:
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
    }
  };

  // Function to get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckIcon className="h-4 w-4 text-green-600" />;
      case 'cancelled':
        return <XIcon className="h-4 w-4 text-red-600" />;
      case 'completed':
        return <ClipboardCheckIcon className="h-4 w-4 text-blue-600" />;
      case 'pending':
      default:
        return <ClockIcon className="h-4 w-4 text-yellow-600" />;
    }
  };

  // If user is not authenticated or not admin, show access denied
  if (!user || user.isAdmin !== "true") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
        <Card className="p-8 w-full max-w-md">
          <h2 className="text-2xl font-bold mb-4 text-center">Access Denied</h2>
          <p className="text-gray-600 mb-6 text-center">
            You don't have permission to access this page.
          </p>
          <div className="flex justify-center">
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-black text-white hover:bg-red-600"
            >
              Return to Homepage
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Admin Header */}
      <div className="bg-black text-white py-4 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto px-4">
          {/* Logo and Back Button Row */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center">
              <div className="flex items-center gap-3">
                <img src={logoImage} alt="Swiss Welding" className="h-12 w-auto" />
                <div className="flex flex-col">
                  <h1 className="text-xl md:text-2xl font-bold">Booking Management</h1>
                  {user && (
                    <div className="text-sm text-primary">
                      Logged in as <span className="font-semibold">{user.username}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Button 
                variant="default" 
                onClick={() => logoutMutation.mutate()}
                className="bg-primary hover:bg-white hover:text-black"
              >
                Logout
              </Button>
            </div>
          </div>
          
          {/* Navigation Links Row */}
          <div className="hidden md:flex justify-center space-x-6 mt-2">
            <button 
              onClick={() => navigate('/')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              HOME
            </button>
            <button 
              onClick={() => navigate('/admin')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              ADMIN
            </button>
            <button 
              className="text-red-500 font-medium border-b-2 border-red-500"
            >
              BOOKINGS
            </button>
            <button 
              onClick={() => navigate('/gallery')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              GALLERY
            </button>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold">Customer Booking Requests</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => refetch()}
              className="border-black text-black hover:bg-black hover:text-white"
            >
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="border-black text-black hover:bg-black hover:text-white"
            >
              Gallery Management
            </Button>
          </div>
        </div>
        
        <Card className="p-6 shadow-md">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full inline-block mb-2"></div>
              <p>Loading booking requests...</p>
            </div>
          ) : !bookings || bookings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-4">No booking requests found.</p>
              <p>When customers make appointment requests, they will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow key={booking.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{booking.id}</TableCell>
                      <TableCell>{booking.name}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1">
                          {booking.service}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3 text-gray-400" /> 
                            {format(parseISO(booking.date), 'MMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1 text-gray-500 text-sm">
                            <ClockIcon className="h-3 w-3 text-gray-400" /> 
                            {booking.time}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 text-sm">
                          <span className="flex items-center gap-1">
                            <MailIcon className="h-3 w-3 text-gray-400" /> 
                            {booking.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <PhoneIcon className="h-3 w-3 text-gray-400" /> 
                            {booking.phone}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span 
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(booking.status)}`}
                        >
                          {getStatusIcon(booking.status)}
                          <span className="ml-1 capitalize">{booking.status}</span>
                        </span>
                      </TableCell>
                      <TableCell>
                        {format(new Date(booking.createdAt), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setNewStatus(booking.status);
                              setStatusUpdateOpen(true);
                            }}
                          >
                            Update
                          </Button>
                          <Button
                            size="sm" 
                            variant="outline"
                            className="h-8 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setResponseDialogOpen(true);
                              
                              // Pre-populate response based on booking status
                              if (booking.status === 'pending') {
                                setResponseMessage(`Dear ${booking.name},\n\nThank you for booking with Swiss Welding. We're reviewing your request for ${booking.service} on ${format(parseISO(booking.date), 'MMMM d, yyyy')} at ${booking.time}.\n\nWe'll be in touch shortly to confirm your appointment.\n\nBest regards,\nSwiss Welding Team`);
                              } else if (booking.status === 'confirmed') {
                                setResponseMessage(`Dear ${booking.name},\n\nWe're pleased to confirm your appointment for ${booking.service} on ${format(parseISO(booking.date), 'MMMM d, yyyy')} at ${booking.time}.\n\nIf you need to reschedule or have any questions, please let us know.\n\nBest regards,\nSwiss Welding Team`);
                              }
                            }}
                          >
                            <MessageSquareIcon className="h-4 w-4 mr-1" />
                            Reply
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
      
      {/* Status Update Dialog */}
      <Dialog 
        open={statusUpdateOpen} 
        onOpenChange={(open) => setStatusUpdateOpen(open)}
      >
        <DialogContent className="sm:max-w-[425px] bg-white border-2 border-gray-200 shadow-lg">
          <DialogHeader className="bg-gray-50 -mx-6 -mt-6 px-6 py-4 border-b border-gray-200">
            <DialogTitle className="text-gray-800">Update Booking Status</DialogTitle>
            <DialogDescription className="text-gray-600">
              Change the status of this booking request.
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="py-4 bg-white">
              <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg border">
                <div>
                  <p className="text-sm font-medium text-gray-600">Customer</p>
                  <p className="font-medium text-gray-800">{selectedBooking.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Service</p>
                  <p className="text-gray-800">{selectedBooking.service}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Date</p>
                  <p className="text-gray-800">{format(parseISO(selectedBooking.date), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Time</p>
                  <p className="text-gray-800">{selectedBooking.time}</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Status</label>
                <Select 
                  value={newStatus} 
                  onValueChange={setNewStatus}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a new status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {newStatus === 'confirmed' && "This will mark the booking as confirmed. You might want to send an email confirmation to the customer."}
                  {newStatus === 'completed' && "This will mark the booking as completed. This is typically done after the service has been performed."}
                  {newStatus === 'cancelled' && "This will mark the booking as cancelled. Please make sure to inform the customer about the cancellation."}
                  {newStatus === 'pending' && "This will mark the booking as pending review."}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setStatusUpdateOpen(false);
                setSelectedBooking(null);
                setNewStatus('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleStatusUpdate}
              disabled={isUpdatingStatus || !newStatus || !!(selectedBooking && newStatus === selectedBooking.status)}
              className="bg-black hover:bg-primary text-white"
            >
              {isUpdatingStatus ? "Updating..." : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Customer Response Dialog */}
      <Dialog 
        open={responseDialogOpen} 
        onOpenChange={(open) => setResponseDialogOpen(open)}
      >
        <DialogContent className="sm:max-w-[525px] bg-white border-2 border-gray-200 shadow-lg">
          <DialogHeader className="bg-gray-50 -mx-6 -mt-6 px-6 py-4 border-b border-gray-200">
            <DialogTitle className="text-gray-800">Respond to Customer</DialogTitle>
            <DialogDescription className="text-gray-600">
              Send a message to the customer regarding their booking request.
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="py-4 bg-white">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Customer</p>
                  <p className="font-medium">{selectedBooking.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Service</p>
                  <p>{selectedBooking.service}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Date</p>
                  <p>{format(parseISO(selectedBooking.date), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Time</p>
                  <p>{selectedBooking.time}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="text-sm">{selectedBooking.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Phone</p>
                  <p className="text-sm">{selectedBooking.phone}</p>
                </div>
              </div>
              
              {selectedBooking.message && (
                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                  <p className="text-sm font-medium text-gray-500 mb-1">Customer Message</p>
                  <p className="text-sm">{selectedBooking.message}</p>
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Response Method</label>
                <div className="flex gap-4">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="email"
                      name="responseMethod"
                      value="email"
                      checked={responseMethod === 'email'}
                      onChange={() => setResponseMethod('email')}
                      className="mr-2"
                    />
                    <label htmlFor="email">Email</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="sms"
                      name="responseMethod"
                      value="sms"
                      checked={responseMethod === 'sms'}
                      onChange={() => setResponseMethod('sms')}
                      className="mr-2"
                    />
                    <label htmlFor="sms">SMS</label>
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Message</label>
                <Textarea
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  rows={8}
                  placeholder="Enter your response to the customer..."
                  className="resize-none"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResponseDialogOpen(false);
                setSelectedBooking(null);
                setResponseMessage('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendResponse}
              disabled={isSending || !responseMessage.trim()}
              className="bg-black hover:bg-primary text-white"
            >
              {isSending ? "Sending..." : "Send Response"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}